import { useEffect, useRef, useState } from 'react';
import { CommentStage, commentsApi } from './api';

/** Invalidation stream: authenticated fetch, no bearer tokens in URLs. */
export function useCommentUpdates(projectId: string, stage: CommentStage, onUpdate: () => void, enabled = true) {
  const updateRef = useRef(onUpdate); updateRef.current = onUpdate;
  const [status, setStatus] = useState<'connecting' | 'live' | 'reconnecting'>('connecting');
  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    let controller: AbortController | null = null;
    let retry: number | undefined;
    let debounce: number | undefined;
    let delay = 1000;
    let generation = 0;
    let live = false;
    const invalidate = () => {
      if (debounce !== undefined) return;
      debounce = window.setTimeout(() => { debounce = undefined; if (!disposed) updateRef.current(); }, 180);
    };
    const connect = async () => {
      if (disposed || document.hidden) return;
      const attempt = ++generation;
      live = false;
      controller?.abort();
      controller = new AbortController();
      try {
        await commentsApi.events(projectId, stage, controller.signal, event => {
          if (disposed || attempt !== generation) return;
          if (event === 'ready') { delay = 1000; live = true; setStatus('live'); invalidate(); }
          if (event === 'comments.changed') invalidate();
        });
      } catch { /* Reconnect and a low-frequency fallback cover transient network failures. */ }
      if (!disposed && attempt === generation && !document.hidden) {
        live = false; setStatus('reconnecting');
        retry = window.setTimeout(connect, delay);
        delay = Math.min(delay * 2, 30000);
      }
    };
    const visibility = () => {
      window.clearTimeout(retry);
      if (document.hidden) { generation += 1; controller?.abort(); }
      else { invalidate(); void connect(); }
    };
    setStatus('connecting');
    void connect();
    document.addEventListener('visibilitychange', visibility);
    const fallback = window.setInterval(() => { if (!document.hidden && !live) invalidate(); }, 30000);
    return () => { disposed = true; generation += 1; controller?.abort(); window.clearTimeout(retry); window.clearTimeout(debounce); window.clearInterval(fallback); document.removeEventListener('visibilitychange', visibility); };
  }, [projectId, stage, enabled]);
  return status;
}
